import React, { useEffect, useState } from "react";
import { ref, get, onValue } from "firebase/database";
import { db,} from "../firebase";

const DashboardHome = ({ gameId }) => {
  const [game, setGame] = useState(null);
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    if (!gameId) return;
    const gameRef = ref(db, `games/${gameId}`);
    get(gameRef).then((snap) => {
      if (snap.exists()) setGame(snap.val());
    });

    // Fetch teams
    const teamsRef = ref(db, `games/${gameId}/teams`);
    const unsubscribe = onValue(teamsRef, (snapshot) => {
      if (snapshot.exists()) {
        const teamsData = snapshot.val();
        const teamsArray = Object.entries(teamsData).map(([id, team]) => ({
          ...team,
          id,
        }));
        setTeams(teamsArray);
      } else {
        setTeams([]);
      }
    });
    return () => unsubscribe();
  }, [gameId]);

  const getStatusBadge = (status) => {
    switch (status) {
      case "upcoming":
        return (
          <span className="badge bg-primary">
            <i className="bi bi-clock-fill me-1"></i>
            Upcoming
          </span>
        );
      case "live":
        return (
          <span className="badge bg-success">
            <i className="bi bi-broadcast me-1"></i>
            Live
          </span>
        );
      case "paused":
        return (
          <span className="badge bg-warning text-dark">
            <i className="bi bi-pause-circle-fill me-1"></i>
            Paused
          </span>
        );
      case "completed":
        return (
          <span className="badge bg-secondary">
            <i className="bi bi-check-circle-fill me-1"></i>
            Completed
          </span>
        );
      default:
        return (
          <span className="badge bg-light text-dark">
            <i className="bi bi-question-circle-fill me-1"></i>
            Not Set
          </span>
        );
    }
  };

  if (!gameId) {
    return (
      <div className="alert alert-warning">
        <i className="bi bi-exclamation-triangle me-2"></i>
        Game ID not found...
      </div>
    );
  }

  // Show overview
  return (
    <div>
      <div className="card shadow mb-4">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="mb-0">
              <i className="bi bi-info-circle-fill me-2"></i>
              Game Overview
            </h4>
          </div>
          <div className="mb-3">
                <div className="card">
                  <div className="card-body p-0">
                    <img
                      src={game?.details?.banner || "/banner1.jpg"}
                      alt="Game Banner"
                      className="img-fluid rounded"
                      style={{
                        width: "100%",
                        maxHeight: "300px",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                </div>
              </div>
          <div className="mb-3">
            <div className="alert alert-info d-flex align-items-center">
              <i className="bi bi-key-fill me-2"></i>
              <div>
                <strong>Game ID:</strong>
                <code className="ms-2 bg-white px-2 py-1 rounded">
                  {gameId}
                </code>
              </div>
            </div>
          </div>

          {game ? (
            <div className="mt-3">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <div className="card bg-light">
                    <div className="card-body">
                      <h6 className="card-subtitle mb-2 text-muted">
                        <i className="bi bi-trophy me-2"></i>Title
                      </h6>
                      <p className="card-text fw-bold">
                        {game.details?.title || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <div className="card bg-light">
                    <div className="card-body">
                      <h6 className="card-subtitle mb-2 text-muted">
                        <i className="bi bi-dribbble me-2"></i>Sport
                      </h6>
                      <p className="card-text fw-bold">
                        {game.auctionSettings?.sport || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <div className="card bg-light">
                    <div className="card-body">
                      <h6 className="card-subtitle mb-2 text-muted">
                        <i className="bi bi-calendar-event me-2"></i>Auction Date
                      </h6>
                      <p className="card-text fw-bold">
                        {game.details?.date
                          ? new Date(game.details.date).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <div className="card bg-light">
                    <div className="card-body">
                      <h6 className="card-subtitle mb-2 text-muted">
                        <i className="bi bi-geo-alt me-2"></i>Location
                      </h6>
                      <p className="card-text fw-bold">
                        {game.details?.location || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <div className="card bg-light">
                    <div className="card-body">
                      <h6 className="card-subtitle mb-2 text-muted">
                        <i className="bi bi-person-badge me-2"></i>Organizer
                      </h6>
                      <p className="card-text fw-bold">
                        {game.details?.organizerName || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <div className="card bg-light">
                    <div className="card-body">
                      <h6 className="card-subtitle mb-2 text-muted">
                        <i className="bi bi-toggle-on me-2"></i>Status
                      </h6>
                      <div className="fs-5">
                        {getStatusBadge(game.details?.status)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <div className="card bg-light">
                    <div className="card-body">
                      <h6 className="card-subtitle mb-2 text-muted">
                        <i className="bi bi-shield-fill me-2"></i>Total Teams
                      </h6>
                      <p className="card-text fw-bold">
                        {game.auctionSettings?.totalTeams || "0"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <div className="card bg-light">
                    <div className="card-body">
                      <h6 className="card-subtitle mb-2 text-muted">
                        <i className="bi bi-wallet2 me-2"></i>Team Purse
                      </h6>
                      <p className="card-text fw-bold text-success">
                        ৳
                        {game.auctionSettings?.purseValue
                          ? Number(
                              game.auctionSettings.purseValue
                            ).toLocaleString()
                          : "0"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Description - Full Width */}
                {game.details?.description && (
                  <div className="col-12 mb-3">
                    <div className="card bg-light">
                      <div className="card-body">
                        <h6 className="card-subtitle mb-2 text-muted">
                          <i className="bi bi-file-text me-2"></i>Description
                        </h6>
                        <p className="card-text">{game.details.description}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Quick Stats */}
              <div className="row mt-4">
                <div className="col-md-4 mb-3">
                  <div className="card bg-primary text-white">
                    <div className="card-body text-center">
                      <i className="bi bi-shield-fill fs-1"></i>
                      <h3 className="mb-0 mt-2">{teams.length}</h3>
                      <p className="mb-0">Teams Created</p>
                    </div>
                  </div>
                </div>

                <div className="col-md-4 mb-3">
                  <div className="card bg-success text-white">
                    <div className="card-body text-center">
                      <i className="bi bi-people-fill fs-1"></i>
                      <h3 className="mb-0 mt-2">
                        {game.auctionSettings?.totalPlayers || "0"}
                      </h3>
                      <p className="mb-0">Total Players</p>
                    </div>
                  </div>
                </div>

                <div className="col-md-4 mb-3">
                  <div className="card bg-info text-white">
                    <div className="card-body text-center">
                      <i className="bi bi-cash-stack fs-1"></i>
                      <h3 className="mb-0 mt-2">
                        {game.auctionSettings?.minBidIncrement ||
                          game.details?.minBidIncrement ||
                          "1000"}
                      </h3>
                      <p className="mb-0">Min Bid Increment</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="alert alert-secondary text-center">
              <i className="bi bi-inbox fs-1 d-block mb-2"></i>
              No data found. Please configure auction settings first.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
